import React, { useState } from 'react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { ThemeProvider } from './components/theme-provider';
import { ThemeToggle } from './components/theme-toggle';
import { ChatAgent } from './components/chat-agent';
import { ChatToggle } from './components/chat-toggle';
import logo from './charlie.jpg';

function App() {
  const [isChatOpen, setIsChatOpen] = useState(false);

  const toggleChat = () => {
    setIsChatOpen(!isChatOpen);
  };

  return (
    <ThemeProvider defaultTheme="system" storageKey="charlie-ui-theme">
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
        {/* Header */}
        <header className="border-b bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm sticky top-0 z-50">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src={logo} 
                alt="Charlie" 
                className="w-10 h-10 rounded-full object-cover border-2 border-primary"
              />
              <h1 className="text-2xl font-bold text-primary">Charlie's Portfolio</h1>
            </div>
            <div className="flex items-center space-x-4">
              <nav className="hidden md:flex space-x-6">
                <a href="#about" className="text-muted-foreground hover:text-primary transition-colors">About</a>
                <a href="#projects" className="text-muted-foreground hover:text-primary transition-colors">Projects</a>
                <a href="#contact" className="text-muted-foreground hover:text-primary transition-colors">Contact</a>
              </nav>
              <ThemeToggle />
              <Button variant="outline" className="md:hidden">
                Menu
              </Button>
            </div>
          </div>
        </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto text-center">
          <div className="flex justify-center mb-8">
            <img 
              src={logo} 
              alt="Charlie" 
              className="w-32 h-32 rounded-full object-cover border-4 border-primary shadow-lg"
            />
          </div>
          <h2 className="text-5xl font-bold text-primary mb-4">
            Hello, I'm Charlie! 👋
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Welcome to my first React app! I'm learning web development and building amazing things. 
            This is my journey into the world of code and beyond!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="text-lg px-8 py-3">
              View My Work
            </Button>
            <Button variant="outline" size="lg" className="text-lg px-8 py-3">
              Get In Touch
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="about" className="py-20 px-4 bg-white dark:bg-slate-900">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-4">What I'm Learning</Badge>
            <h3 className="text-3xl font-bold text-primary mb-4">My Development Journey</h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              I'm passionate about creating beautiful, functional web applications. 
              Here's what I'm currently exploring in my coding journey.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">⚛️</span>
                </div>
                <CardTitle>React Development</CardTitle>
                <CardDescription>
                  Building interactive user interfaces with React and modern JavaScript
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Learning component-based architecture, state management, and modern React patterns.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🎨</span>
                </div>
                <CardTitle>UI/UX Design</CardTitle>
                <CardDescription>
                  Creating beautiful and accessible user experiences
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Exploring design systems, accessibility, and user-centered design principles.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🚀</span>
                </div>
                <CardTitle>Modern Tools</CardTitle>
                <CardDescription>
                  Working with the latest development tools and frameworks
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Using TypeScript, Tailwind CSS, and modern build tools for efficient development.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-20 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-4">Featured Work</Badge>
            <h3 className="text-3xl font-bold text-primary mb-4">My Projects</h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Here are some of the projects I've been working on as I learn web development.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="overflow-hidden hover:shadow-lg transition-shadow">
              <div className="h-48 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <span className="text-white text-4xl">📱</span>
              </div>
              <CardHeader>
                <CardTitle>React Portfolio</CardTitle>
                <CardDescription>
                  This very website you're looking at! Built with React, TypeScript, and shadcn/ui.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge variant="outline">React</Badge>
                  <Badge variant="outline">TypeScript</Badge>
                  <Badge variant="outline">Tailwind CSS</Badge>
                  <Badge variant="outline">shadcn/ui</Badge>
                </div>
                <Button variant="outline" size="sm">
                  View Project
                </Button>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-lg transition-shadow">
              <div className="h-48 bg-gradient-to-br from-green-500 to-teal-600 flex items-center justify-center">
                <span className="text-white text-4xl">🌱</span>
              </div>
              <CardHeader>
                <CardTitle>Learning Projects</CardTitle>
                <CardDescription>
                  Various small projects and experiments as I explore different technologies.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge variant="outline">JavaScript</Badge>
                  <Badge variant="outline">HTML/CSS</Badge>
                  <Badge variant="outline">Git</Badge>
                </div>
                <Button variant="outline" size="sm">
                  Coming Soon
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 bg-white dark:bg-slate-900">
        <div className="container mx-auto text-center">
          <Badge variant="secondary" className="mb-4">Get In Touch</Badge>
          <h3 className="text-3xl font-bold text-primary mb-4">Let's Connect!</h3>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
            I'm always excited to connect with fellow developers and learn from the community. 
            Feel free to reach out!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="text-lg px-8 py-3">
              <a href="https://reactjs.org" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                Learn React
                <span className="text-sm">↗</span>
              </a>
            </Button>
            <Button variant="outline" size="lg" className="text-lg px-8 py-3">
              Send Message
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-muted/50 py-8 px-4">
        <div className="container mx-auto text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <img 
              src={logo} 
              alt="Charlie" 
              className="w-8 h-8 rounded-full object-cover"
            />
            <span className="text-muted-foreground">Made with ❤️ by Charlie</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Built with React, TypeScript, Tailwind CSS, and shadcn/ui
          </p>
        </div>
      </footer>
      
      {/* Chat Components */}
      <ChatToggle onClick={toggleChat} isOpen={isChatOpen} />
      <ChatAgent isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
      </div>
    </ThemeProvider>
  );
}

export default App;